import { CampaignParticipantStatusPicker } from 'views/pickers';
import PropTypes from 'prop-types';
import { TextModal } from 'views/components';
import isEmpty from 'lodash/isEmpty';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
    CAMPAIGN_TYPES,
    campaignActions,
    parseCampaignParticipantStatus
} from 'core/Campaign';
import {
    Col,
    Row
} from 'reactstrap';
import {
    Container,
    DownloadModal,
    FormGroup,
    IconButton,
    PageController,
    Table,
    Text
} from 'mol-ui';
import {
    DonationModal,
    InvitationModal,
    TrainingModal,
    VaccinationModal
} from '../';
import React, { useEffect, useState } from 'react';
import {
    useArray,
    useCopyToClipboard,
    useDispatch,
    useObjectState,
    useSelector,
    useTableOrder,
    useToggle
} from 'hooks';

const ABSENT = 'absent';
const ACCEPTED = 'accepted';
const INVITED = 'invited';
const PRESENT = 'present';
const REGISTERED = 'registered';
const REJECTED = 'rejected';

const CampaignParticipants = ({
    id
}) => {

    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { successColor } = useTheme();
    
    const {
        donationSuccess,
        participants,
        participantsDownload,
        participantsDownloading,
        participantsLoading,
        participantsStatus,
        processing,
        selected,
        trainingSuccess,
        vaccinationSuccess
    } = useSelector('campaign');
    const {
        invitation_link,
        type,
        vaccine
    } = selected ?? {};
    const { id: vaccine_id } = vaccine ?? {};

    const [copyToClipboard, copiedToClipboard] = useCopyToClipboard();
    const [currentPage, setCurrentPage, { replaceValues }] = useArray([], { type: 'object' });
    const [currentTotals, setCurrentTotals, , setCurrentTotalsValues] = useObjectState({});
    const [donation, setDonation] = useState(null);
    const [downloadModal, toggleDownloadModal] = useToggle(false);
    const [invitationModal, toggleInvitationModal] = useToggle(false);
    const [orderBy, orderWay, changeOrder, orderIcon] = useTableOrder();
    const [participantStatus, setParticipantStatus] = useState('');
    const [rejectedComment, setRejectedComment] = useState('');
    const [training, setTraining] = useState(null);
    const [vaccination, setVaccination] = useState(null);

    const PARTICIPANT_HEADERS = [{
        key: 'last_name',
        label: t('Apellido'),
        orderKey: 'last_name'
    }, {
        key: 'first_name',
        label: t('Nombre'),
        orderKey: 'first_name'
    },  {
        key: 'identification',
        label: t('DNIPaciente')
    }, {
        key: 'workplace',
        label: t('Lugar de trabajo'),
        orderKey: 'workplace'
    }, {
        key: 'position',
        label: t('Cargo'),
        orderKey: 'position'
    }, {
        format: ({ status }) => ({
            ...parseCampaignParticipantStatus(status)?.style,
            center: true
        }),
        key: 'status_label',
        label: t('Estado')
    }].map(p => ({
        ...p,
        icon: orderIcon(p?.orderKey),
        onClick: !p.orderKey || participantsLoading ? undefined : () => changeOrder(p.key)
    }));

    useEffect(() => {
        dispatch(campaignActions.getCampaignParticipants({ id }));
    }, [id]);

    useEffect(() => {
        setCurrentPage(participants?.getData()?.data ?? []);
    }, [participants]);

    useEffect(() => {
        setCurrentTotals(participantsStatus);
    }, [participantsStatus]);

    useEffect(() => {
        dispatch(campaignActions.getCampaignParticipants({
            id,
            ...(!!participantStatus && { status: participantStatus }),
            ...(orderBy && { order_by: orderBy }),
            ...(orderWay && { order_way: orderWay })
        }));
    }, [orderBy, orderWay]);

    useEffect(() => {
        if (!donationSuccess) return;
        const { participant_id } = donation;
        replaceValues({ donation_id: donationSuccess }, 'id', participant_id);
        dispatch(campaignActions.setCampaignDonationSuccess(null));
        setDonation(null);
    }, [donation, donationSuccess]);

    useEffect(() => {
        if (!trainingSuccess) return;
        const { participant_id } = training;
        replaceValues({ training_id: trainingSuccess }, 'id', participant_id);
        dispatch(campaignActions.setCampaignTrainingSuccess(null));
        setTraining(null);
    }, [training, trainingSuccess]);

    useEffect(() => {
        if (!vaccinationSuccess) return;
        const { participant_id } = vaccination;
        replaceValues({ vaccination_id: vaccinationSuccess }, 'id', participant_id);
        dispatch(campaignActions.setCampaignVaccinationSuccess(null));
        setVaccination(null);
    }, [vaccination, vaccinationSuccess]);

    const getStatusData = key => ({
        label: parseCampaignParticipantStatus(key)?.label,
        value: (currentTotals ?? {})[key] ?? 0
    });

    const handleParticipantFilterStatusChange = value => {
        setParticipantStatus(value);
        dispatch(campaignActions.getCampaignParticipants({
            id,
            ...(!!value && { status: [value] }),
            ...(orderBy && { order_by: orderBy }),
            ...(orderWay && { order_way: orderWay })
        }));
    };

    const handleStatusChange = (participant_id, status, preStatus) => {
        dispatch(campaignActions.changeParticipantStatus({
            participant_id,
            status
        }));
        replaceValues({
            status,
            status_label: parseCampaignParticipantStatus(status)?.label
        }, 'id', participant_id);
        setCurrentTotalsValues({
            [preStatus]: currentTotals[preStatus] - 1,
            [status]: (currentTotals[status] ?? 0) + 1
        });
    };

    const actionHandler = [{
        disabled: () => processing,
        display: ({ status }) => status === REGISTERED && type !== CAMPAIGN_TYPES.healthSurvey,
        icon: 'done',
        onClick: ({ id, status }) => handleStatusChange(id, ACCEPTED, status),
        title: t('Aceptar')
    }, {
        disabled: () => processing,
        display: ({ status }) => status === REGISTERED && type !== CAMPAIGN_TYPES.healthSurvey,
        icon: 'close',
        onClick: ({ id, status }) => handleStatusChange(id, REJECTED, status),
        title: t('Rechazar')
    }, {
        disabled: () => processing,
        display: ({ status }) => [ABSENT, ACCEPTED, PRESENT, REJECTED].includes(status) && type !== CAMPAIGN_TYPES.healthSurvey,
        icon: 'fast_rewind',
        onClick: ({ id, status }) => handleStatusChange(id, [ACCEPTED, REJECTED].includes(status) ? REGISTERED : ACCEPTED, status),
        title: t('Volver a marcar como registrado')
    }, {
        disabled: () => processing,
        display: ({ status }) => status === ACCEPTED && type !== CAMPAIGN_TYPES.healthSurvey,
        icon: 'person',
        onClick: ({ id, status }) => handleStatusChange(id, PRESENT, status),
        title: t('Marcar presente')
    }, {
        disabled: () => processing,
        display: ({ status }) => status === ACCEPTED && type !== CAMPAIGN_TYPES.healthSurvey,
        icon: 'person_off',
        onClick: ({ id, status }) => handleStatusChange(id, ABSENT, status),
        title: t('Marcar ausente')
    }, {
        color: ({ donation_id }) => donation_id ? successColor : undefined,
        disabled: () => processing,
        display: ({ status }) => status === PRESENT && type === CAMPAIGN_TYPES.donation,
        icon: 'attachment',
        onClick: ({ donation_id, id }) => setDonation({
            donation_id: donation_id ?? 0,
            participant_id: id
        }),
        title: t('Ver donación')
    }, {
        color: ({ vaccination_id }) => vaccination_id ? successColor : undefined,
        disabled: () => processing,
        display: ({ status }) => status === PRESENT && type === CAMPAIGN_TYPES.vaccination,
        icon: 'attachment',
        onClick: ({ vaccination_id, id }) => setVaccination({
            participant_id: id,
            vaccination_id: vaccination_id ?? 0
        }),
        title: t('Ver vacunación')
    }, {
        color: ({ training_id }) => training_id ? successColor : undefined,
        disabled: () => processing,
        display: ({ status }) => status === PRESENT && type === CAMPAIGN_TYPES.training,
        icon: 'attachment',
        onClick: ({ training_id, id }) => setTraining({
            participant_id: id,
            training_id: training_id ?? 0
        }),
        title: t('Ver capacitación')
    }, {
        disabled: () => processing,
        display: ({ rejected_comment, status }) => status === REJECTED && !!rejected_comment,
        icon: 'message',
        onClick: ({ rejected_comment }) => setRejectedComment(rejected_comment),
        title: t('Ver comentario')
    }];

    const refreshParticipantsList = () => {
        dispatch(campaignActions.getCampaignParticipants({
            id,
            ...(!!participantStatus && { status: [participantStatus] })
        }));
    };

    const statusKeys = [INVITED, REGISTERED, ACCEPTED, REJECTED, PRESENT, ABSENT];

    return (
        <Container>
            <DownloadModal
                active={ downloadModal }
                data={ participantsDownload }
                fileName={ t('Participantes') }
                headers={ PARTICIPANT_HEADERS }
                loading={ participantsDownloading }
                onFetchData={ () => dispatch(campaignActions.getCampaignParticipantsDownload({
                    id,
                    ...(participantStatus && { status: [participantStatus] })
                })) }
                onToggle={ toggleDownloadModal }
            />
            <DonationModal
                active={ !isEmpty(donation) }
                id={ donation?.donation_id }
                participantId={ donation?.participant_id }
                onToggle={ () => setDonation(null) }
            />
            <VaccinationModal
                active={ !isEmpty(vaccination) }
                id={ vaccination?.vaccination_id }
                participantId={ vaccination?.participant_id }
                vaccineId={ vaccine_id }
                onToggle={ () => setVaccination(null) }
            />
            <TrainingModal
                active={ !isEmpty(training) }
                id={ training?.training_id }
                participantId={ training?.participant_id }
                onToggle={ () => setTraining(null) }
            />
            <TextModal
                active={ !!rejectedComment }
                text={ rejectedComment }
                onToggle={ () => setRejectedComment('') }
            />
            <Container alignItems="center" flexDirection="row">
                <IconButton
                    icon="open_in_new"
                    label={ t('Link de inscripción del paciente') }
                    size={ 14 }
                    onClick={ () => window.open(invitation_link, '_blank') }
                />
                <IconButton
                    icon={ copiedToClipboard ? 'done' : 'content_copy' }
                    size={ 14 }
                    onClick={ copiedToClipboard ? () => null : () => copyToClipboard(invitation_link) }
                />
            </Container>
            <IconButton
                icon="add"
                label={ t('Agregar participantes') }
                margin="10px 0px 0px 0px"
                size={ 14 }
                onClick={ toggleInvitationModal }
            />
            <InvitationModal
                active={ invitationModal }
                id={ id }
                onClose={ toggleInvitationModal }
            />
            <FormGroup label={ t('Estado') } margin="10px 0px" width="250px">
                <CampaignParticipantStatusPicker
                    margin="0px"
                    value={ participantStatus }
                    onChange={ handleParticipantFilterStatusChange }
                />
            </FormGroup>
            <IconButton
                icon="download"
                label={ t('Descargar listado') }
                margin="0px 0px 10px 0px"
                size={ 14 }
                onClick={ toggleDownloadModal }
            />
            <IconButton
                icon="refresh"
                label={ t('Actualizar') }
                size={ 14 }
                onClick={ refreshParticipantsList }
            />
            <FormGroup label={ t('Totales por estado') } margin="10px 0px 0px 0px">
                <Row>
                    {
                        statusKeys.map(key => {
                            const { label, value } = getStatusData(key);
                            return (
                                <Col key={ key } md="3" xs="6">
                                    <Text margin="0px 0px 5px 0px">{ `${ label }: ${ value }` }</Text>
                                </Col>
                            );
                        })
                    }
                </Row>
            </FormGroup>
            <Table
                config={ PARTICIPANT_HEADERS }
                data={ currentPage }
                loading={ participantsLoading }
                margin="10px 0px"
                rowActions={ actionHandler }
            />
            <PageController
                controller={ participants }
            />
        </Container>
    );
};

CampaignParticipants.propTypes = {
    id: PropTypes.number
};

export default CampaignParticipants;
